/// <amd-module name="TFS/Admin/Groups/Dialogs/MembershipAddPanel/MembershipAddPanel" />
import * as React from "react";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IButtonProps } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { IdentityPicker, IIdentity, IPeoplePickerProvider, IdentityType } from "VSSUI/IdentityPicker";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Panel } from "VSSUI/Panel";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { VssComponent } from "VSS/Platform/Layout";
import { GroupsComponent } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { filterXSS } from "xss";
import { IOperationScope } from "TFS/Admin/Groups/Common/Constants";
import { validateEmail } from "TFS/Admin/Common/Util/EmailValidator";
import { CustomPeoplePickerProvider } from "TFS/Admin/Common/Providers/CustomPeoplePickerProvider";
import { MembershipAddPanelProps } from "TFS/Admin/Groups/Dialogs/MembershipAddPanel/MembershipAddPanel.Props";
import { ObservableValue } from "VSS/Core/Observable";
import { FormItem } from "VSSUI/FormItem";
import { Checkbox } from "VSSUI/Checkbox";
import { FormatComponent } from "VSS/Platform/Components/Format";

interface MembershipAddPanelState {
    isSaving: boolean;
    invalidMembers: IIdentity[];
    validMembers: IIdentity[];
}

export class MembershipAddPanel extends React.Component<MembershipAddPanelProps, MembershipAddPanelState> {
    private _pickerProvider: IPeoplePickerProvider;
    private _enableSaveButton: boolean = false;
    private _adminAsMember = new ObservableValue<boolean>(true);

    constructor(props: MembershipAddPanelProps) {
        super(props);
        const { pageContext, operationScopes, identityTypes, validMembers, invalidMembers } = this.props;
        this._pickerProvider =
            operationScopes && operationScopes.find(x => x === IOperationScope.MSA)
                ? new CustomPeoplePickerProvider(pageContext, identityTypes, operationScopes)
                : new PeoplePickerProvider(pageContext, { identityTypes, options: { MinResults: 5, MaxResults: 20 }, operationScopes });

        this.state = {
            isSaving: false,
            validMembers,
            invalidMembers
        };
    }

    public render(): JSX.Element {
        this._enableSaveButton =
            !this.state.isSaving &&
            this.props.validMembers.length > 0 &&
            this.props.invalidMembers.length === 0 &&
            this._allPermissionsFetched() &&
            this._isManagerMembershipAllowedForSelectedGroups();
        return (
            <Panel
                size={ContentSize.Medium}
                onDismiss={this.props.onDismiss}
                titleProps={{ text: this.props.panelTitle }}
                description={this.props.description}
                footerButtonProps={this._getFooterButtons()}
                defaultActiveElement=".permissions-members-add-wrapper input"
            >
                <div className="permissions-members-add-wrapper rhythm-vertical-20">
                    <FormItem
                        className="group-add-item"
                        label={this.props.peoplePickerLabel}
                        message={this._getErrorMessage()}
                        error={this.props.invalidMembers.length > 0}
                    >
                        <IdentityPicker
                            pickerProvider={this._pickerProvider}
                            onIdentityAdded={this._onIdentityAdded}
                            onIdentitiesRemoved={this._onIdentitiesRemoved}
                            onIdentityRemoved={this._onIdentityRemoved}
                            selectedIdentities={this.props.validMembers}
                            placeholderText={this.props.peoplePickerPlaceholder}
                            onResolveEntity={this._onResolveEmail}
                        />
                    </FormItem>
                    {this._allPermissionsFetched() && !this._isManagerMembershipAllowedForSelectedGroups() ? (
                        <MessageCard className="permissions-card" severity={MessageCardSeverity.Error}>
                            {Resources.AddMemberOfFailedDueToPermissions}
                        </MessageCard>
                    ) : null}
                    {this.props.isAdminsPage && (
                        <Checkbox onChange={this._onAdminAsMemberChanged} checked={this._adminAsMember} label={Resources.AdminMemberLabel} />
                    )}
                </div>
                {this.state.isSaving ? (
                    <div className="flex flex-column flex-center justify-center overlay-spinner">
                        <Spinner size={SpinnerSize.large} label={Resources.SavingSpinnerLabel} />
                    </div>
                ) : null}
            </Panel>
        );
    }

    private _onAdminAsMemberChanged = (ev: any, checked: boolean) => {
        this._adminAsMember.value = checked;
    };

    private _isManagerMembershipAllowedForSelectedGroups(): boolean {
        if (!this.props.getPermissions) return true;
        const { permissions } = this.props;
        for (let identity of this.props.validMembers) {
            if (
                identity.subjectDescriptor &&
                permissions[identity.subjectDescriptor] &&
                !permissions[identity.subjectDescriptor].isManageMembershipAllowed
            ) {
                return false;
            }
        }
        return true;
    }

    private _allPermissionsFetched(): boolean {
        if (!this.props.getPermissions) return true;
        const { permissions } = this.props;
        for (let identity of this.props.validMembers) {
            if (identity.subjectDescriptor && !permissions[identity.subjectDescriptor]) {
                return false;
            }
        }
        return true;
    }

    private _getFooterButtons = () => {
        const footerButtons: IButtonProps[] = [
            {
                disabled: false,
                onClick: this.props.onDismiss,
                text: Resources.Cancel
            },
            {
                className: "primary-action-button",
                disabled: !this._enableSaveButton,
                onClick: this._onSave,
                primary: true,
                text: Resources.Save
            }
        ];

        return footerButtons;
    };

    private _onResolveEmail = (email: string, identity: IIdentity | null): void => {
        const { validMembers, invalidMembers } = this.props;
        const index = validMembers.findIndex(x => x.originDirectory == "email" && x.entityId == email);
        if (index === -1) return;
        validMembers.splice(index, 1);
        invalidMembers.splice(
            invalidMembers.findIndex(x => x.originDirectory == "email" && x.entityId == email),
            1
        );
        this.setState({ validMembers, invalidMembers });
        if (identity) {
            this.props.checkMembershipExistence(identity);
            if (this.props.getPermissions && identity.subjectDescriptor && !this.props.permissions[identity.subjectDescriptor]) {
                this.props.onLogTelemetry(GroupsComponent.MemberOfAddPanel, "Verify permissions for selected group");
                this.props.getPermissions(identity.subjectDescriptor);
            } else {
                this.props.onLogTelemetry(this._getPermissionComponent(), "Identitypicker direct select group");
            }
        }
    };

    private _onIdentityAdded = (item: IIdentity): void => {
        // If the identity is a MSA user, check that the email is valid.
        if (item.originDirectory === IdentityType.Custom) {
            if (!validateEmail(item.entityId)) {
                this.props.invalidMembers.push(item);
                this.props.validMembers.push(item);
                this.setState({ validMembers: this.props.validMembers, invalidMembers: this.props.invalidMembers });
            } else {
                this.props.validMembers.push(item);
                this.setState({ validMembers: this.props.validMembers });
            }
        } else if (item.originDirectory == "email") {
            const { validMembers, invalidMembers } = this.props;
            if (validMembers.findIndex(x => x.entityId === item.entityId) === -1) {
                validMembers.push(item);
                invalidMembers.push(item);
                this.setState({ validMembers, invalidMembers });
            }
        } else {
            // Check if the added identity is already a member.
            this.props.checkMembershipExistence(item);
        }

        if (this.props.getPermissions && item.subjectDescriptor && !this.props.permissions[item.subjectDescriptor]) {
            this.props.onLogTelemetry(GroupsComponent.MemberOfAddPanel, "Verify permissions for selected group");
            this.props.getPermissions(item.subjectDescriptor);
        } else {
            this.props.onLogTelemetry(this._getPermissionComponent(), "Identitypicker direct select group");
        }
    };

    private _onIdentitiesRemoved = (items: IIdentity[]): void => {
        this.props.onLogTelemetry(this._getPermissionComponent(), "Identitypicker removed groups");
        this.props.removeMembers(items);
    };

    private _onIdentityRemoved = (item: IIdentity): void => {
        this.props.onLogTelemetry(this._getPermissionComponent(), "Identitypicker removed group");
        this.props.removeMembers([item]);
    };

    private _getErrorMessage = (): JSX.Element => {
        const { invalidMembers, isAdminsPage } = this.props;
        if (invalidMembers.length == 0) {
            return this._formatMessage("");
        }
        const [email, other] = invalidMembers.reduce(
            ([mail, other], e) => {
                (e.originDirectory === IdentityType.Custom || e.originDirectory == "email" ? mail : other).push(e.displayName);
                return [mail, other];
            },
            [[], []]
        );

        if (email.length > 0 && other.length > 0) {
            return this._formatMessage(
                Resources.MemberInGroupAndMemberEmailError,
                filterXSS(other.join(", ")),
                filterXSS(email.join(", "))
            );
        } else if (email.length > 0) {
            return this._formatMessage(Resources.ErrorInMemberEmail, filterXSS(email.join(", ")));
        }
        return this._formatMessage(
            isAdminsPage ? Resources.MemberAlreadyAnAdminError : Resources.MemberAlreadyInGroupError,
            filterXSS(other.join(", "))
        );
    };

    private _formatMessage(format: string, ...args: string[]) {
        return (
            <FormatComponent format={format}>
                {args.map((x, i) => (
                    <b key={`key-${i}`}>{x}</b>
                ))}
            </FormatComponent>
        );
    }

    private _onSave = () => {
        this.props.onLogTelemetry(GroupsComponent.MemberOfAddPanel, "Save group membership");
        this.setState({ isSaving: true });
        this.props.onSave(this.props.validMembers, this._adminAsMember.value);
    };

    private _getPermissionComponent(): GroupsComponent {
        return this.props.getPermissions ? GroupsComponent.MemberOfAddPanel : GroupsComponent.MemberAddPanel;
    }
}

VssComponent.register("ms.vss-admin-web.orggroups-membership-add-panel", MembershipAddPanel);
